/**
 * RelationshipBadge — surfaces a user's current relationship (date / engaged /
 * married) on their Profile / PublicProfile card. On the OWN profile it also
 * exposes an "End relationship" button (unilateral — either partner can end
 * the bond and the other side is notified via DM + toast).
 *
 * Props:
 *   relationship  { kind, since, partner: {user_id, displayName, avatar, flag} }
 *   isSelf        boolean — controls the "End" affordance
 *   onEnded       () => void  — called after a successful end so the parent
 *                  can refresh the profile without a page reload
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Diamond, Crown, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from './ui/alert-dialog';

const KIND_META = {
  date:    { icon: Heart,   label: 'Dating',  color: '#F472B6', bg: 'from-rose-500/25 to-pink-500/10',       ring: 'ring-rose-400/50' },
  engaged: { icon: Diamond, label: 'Engaged', color: '#22D3EE', bg: 'from-cyan-500/25 to-fuchsia-500/10',    ring: 'ring-cyan-400/50' },
  married: { icon: Crown,   label: 'Married', color: '#FBBF24', bg: 'from-amber-500/25 to-orange-500/10',    ring: 'ring-amber-400/60' },
};

function formatSince(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

export default function RelationshipBadge({ relationship, isSelf = false, onEnded }) {
  const [busy, setBusy] = useState(false);
  if (!relationship || !relationship.kind) return null;
  const meta = KIND_META[relationship.kind] || KIND_META.date;
  const Icon = meta.icon;
  const partner = relationship.partner;
  const since = formatSince(relationship.since);

  const endNow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post('/dating/relationship/end');
      toast.success('Relationship ended', { description: `You are single again.` });
      onEnded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not end relationship');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border border-white/10 bg-gradient-to-br ${meta.bg} p-4 flex items-center gap-3`}
      data-testid={`relationship-badge-${relationship.kind}`}
    >
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
        style={{ background: `${meta.color}25`, boxShadow: `inset 0 0 0 2px ${meta.color}55` }}
      >
        <Icon size={20} style={{ color: meta.color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: meta.color }}>
          {meta.label}
        </div>
        <div className="mt-0.5 flex items-center gap-2 min-w-0">
          {partner ? (
            <Link
              to={`/app/users/${partner.user_id}`}
              className="flex items-center gap-2 min-w-0 hover:opacity-90 transition"
              data-testid="relationship-partner-link"
            >
              <Avatar className={`w-7 h-7 ring-2 ${meta.ring}`}>
                <AvatarImage src={partner.avatar} />
                <AvatarFallback className="bg-white/10 text-[10px]">
                  {partner.displayName?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">
                  {partner.displayName}
                  {partner.flag ? <span className="ml-1">{partner.flag}</span> : null}
                </div>
                {since && <div className="text-[11px] text-white/50 leading-tight">Since {since}</div>}
              </div>
            </Link>
          ) : (
            <div className="text-sm text-white/70">In a {relationship.kind} relationship</div>
          )}
        </div>
      </div>
      {isSelf && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              disabled={busy}
              data-testid="relationship-end-btn"
              className="text-[11px] font-medium px-2.5 py-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 border border-white/10 transition inline-flex items-center gap-1"
            >
              <X size={11} /> End
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#161622] border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display">End this relationship?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/70">
                {partner
                  ? `You'll no longer be listed as ${meta.label.toLowerCase()} to ${partner.displayName}. They'll be notified. You can propose again later.`
                  : 'Your relationship badge will be cleared. This cannot be undone.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10" data-testid="relationship-end-cancel">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={endNow}
                data-testid="relationship-end-confirm"
                className="bg-rose-500 hover:bg-rose-600 text-white border-0"
              >
                Yes, end it
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
